import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FileText, Users, Stethoscope, CheckCircle2, XCircle, ArrowRight, Calculator, Shield } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const HERO_IMG = "/veterans-disability-education-va-claim-evidence-checklist.jpg";

const RESULTS = [
  {
    header: "Army veteran · First-time filer",
    body: "Waited 20 years to file, assuming it was too late. We built the claim around his cervical spine condition and bilateral radiculopathy before filing. Result: 70% combined — on the first decision.",
  },
  {
    header: "Veteran stuck at 90% · Claim for increase",
    body: "Already at 90%, where the VA math makes 100% hardest to reach. We built an increase strategy around her migraines. Result: 100%, Permanent & Total status, and Chapter 35 education benefits for her dependents.",
  },
  {
    header: "Veteran with paid representation · Rebuilt claim",
    body: "Spent years with a law firm and came out with 10%. We went through his file, found what the evidence actually supported, and rebuilt the strategy. Result: 100% Permanent & Total and five figures in retroactive pay.",
  },
  {
    header: "Veteran at 30% · PTSD increase + secondaries",
    body: "Rated 30% with conditions diagnosed but never claimed. We structured the increase and added the missing conditions. Result: 80% combined.",
  },
];

const METHOD = [
  { icon: FileText, t: "Lay statements the VA can actually use", d: "Most veterans' statements read like personal essays. We rewrite them to hit dates, in-service events, symptoms, and functional impact, the four things the VA weighs." },
  { icon: Stethoscope, t: "Strong nexus letters", d: "The single most important document in most claims. We help you get one written to the 'at least as likely as not' standard using independent providers." },
  { icon: Users, t: "Evidence from records and witnesses", d: "Your medical records and buddy statements hold evidence you can't see yet. We show you where to look and how to organize what you find." },
];

const YES = ["Filing a first VA disability claim", "Seeking a rating increase or filing for a secondary condition", "Appealing a denial or a lowball rating", "Working toward 100% or P&T", "Willing to gather records and write statements with guidance"];
const NO = ["Looking for someone to file the claim for you (see an accredited VSO)", "Expecting a guaranteed rating outcome", "Not willing to do the evidence work"];

export default function Landing() {
  useSeo({
    title: "Veterans Disability Education | VA Claim Strategy from a 100% Rated Army Veteran",
    description: "Learn how VA disability claims are actually won. Education and claim strategy from a 100% rated U.S. Army veteran. Free 20-minute consult.",
  });
  return (
    <div>
      {/* HERO */}
      <section className="bg-primary text-primary-foreground relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="label-caps text-accent mb-5">Real strategy, not guesswork</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-primary-foreground">
              The strongest VA claims are built <span className="text-accent">before they're filed.</span>
            </h1>
            <p className="mt-7 text-lg lg:text-xl text-primary-foreground/90 max-w-2xl leading-relaxed">
              I'm <span className="font-semibold text-white">Pamela J. Booker</span>, a 100% rated U.S. Army veteran.
              Since I got service-connected, I've helped 20+ veterans get rated or reach 100% by teaching them what evidence actually wins claims.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" data-testid="hero-cta-calc">
                <Link to="/calculator"><Calculator className="w-5 h-5" /> Calculate Your VA Pay</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" data-testid="hero-cta-consult">
                <Link to="/intake">Start Your Free Consult <ArrowRight className="w-4 h-4" /></Link>
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-3 text-sm text-primary-foreground/80">
              <Shield className="w-4 h-4 text-accent" />
              20-minute consult · no fee · no pressure
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden bg-accent/10 border-4 border-accent">
                <img src={HERO_IMG} alt="Veterans Disability Education — VA claim evidence checklist, medical records, and personal statement" className="w-full h-full object-cover" data-testid="hero-portrait" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden lg:block bg-accent text-accent-foreground px-6 py-5 max-w-[260px]">
                <div className="font-display text-2xl leading-tight">20+ veterans coached</div>
                <div className="text-sm mt-1 font-semibold">Service-connected. Increased. Rated 100%.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
          <div className="max-w-3xl mb-12">
            <div className="label-caps mb-4">The VDE Method</div>
            <h2 className="font-display text-3xl lg:text-5xl">Three evidence pillars every winning claim stands on.</h2>
            <p className="mt-5 text-lg text-primary/80">Most veterans lose the rating they deserve, not because they aren't disabled, but because their file doesn't say what the VA needs to hear. We fix the file.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {METHOD.map((m, i) => (
              <div key={i} className="bg-secondary p-8 border-l-4 border-accent" data-testid={`method-${i}`}>
                <m.icon className="w-8 h-8 text-primary mb-5" strokeWidth={1.7} />
                <h3 className="font-display text-xl mb-3 leading-snug">{m.t}</h3>
                <p className="text-[15px] text-primary/80 leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO */}
      <section className="bg-secondary">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
          <div className="max-w-3xl mb-12">
            <div className="label-caps mb-4">Who this is for</div>
            <h2 className="font-display text-3xl lg:text-5xl">Straight talk before we start.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 border-t-4 border-primary" data-testid="who-yes">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle2 className="w-7 h-7 text-primary" />
                <h3 className="font-display text-2xl">A fit if you're…</h3>
              </div>
              <ul className="space-y-3">
                {YES.map((t, i) => (
                  <li key={i} className="flex gap-3 text-[16px]">
                    <span className="text-accent mt-1">▸</span><span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 border-t-4 border-primary/40" data-testid="who-no">
              <div className="flex items-center gap-3 mb-6">
                <XCircle className="w-7 h-7 text-primary/60" />
                <h3 className="font-display text-2xl text-primary/80">Not a fit if you're…</h3>
              </div>
              <ul className="space-y-3">
                {NO.map((t, i) => (
                  <li key={i} className="flex gap-3 text-[16px] text-primary/80">
                    <span className="text-primary/40 mt-1">▸</span><span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS SO FAR */}
      <section className="bg-white" data-testid="results-section">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
          <div className="max-w-3xl mb-4">
            <div className="label-caps mb-4">Results so far</div>
            <h2 className="font-display text-3xl lg:text-5xl" data-testid="results-heading">
              Real veterans. Real outcomes. Names withheld, results verified.
            </h2>
          </div>
          <p className="max-w-3xl text-lg text-primary/80 mb-12 leading-relaxed" data-testid="results-intro">
            Every veteran I work with decides for themselves whether to be quoted publicly. Until they do, here's what the work has produced — no names, no embellishment.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {RESULTS.map((r, i) => (
              <div key={i} className="bg-secondary p-8 border-l-4 border-accent" data-testid={`result-${i}`}>
                <div className="text-5xl text-accent font-display leading-none mb-5" aria-hidden>"</div>
                <div className="label-caps text-primary/70 mb-3" data-testid={`result-${i}-header`}>{r.header}</div>
                <p className="text-[16px] text-primary/90 leading-relaxed" data-testid={`result-${i}-body`}>{r.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-primary/60 leading-relaxed max-w-3xl" data-testid="results-disclaimer">
            Individual results. Every claim is different, and no outcome is ever guaranteed.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="gold-rule mb-6"></div>
            <h2 className="font-display text-3xl lg:text-5xl">Bring me your file. I'll tell you what's missing.</h2>
            <p className="mt-5 text-lg text-primary-foreground/85 max-w-2xl">Start with a free 20-minute consult. No pitch. No pressure. Just an honest read on whether I can help.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Button asChild size="lg" data-testid="landing-final-cta">
              <Link to="/intake">Start Your Free Consult <ArrowRight className="w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
